// Plans + issued keys (master email only).
//
// A plan is a bundle of capability switches carried as claims in every lease
// it backs. Editing a plan (or a key's plan / overrides) takes effect on each
// bound hub's next lease refresh — no hub release. Only names in the
// capability catalog can be used: those are the switches shipped hubs know.

import React, { useEffect, useState } from 'react';
import { Plus, Save, ChevronUp } from 'lucide-react';
import { api } from '../services/api';
import { LicenseKeyDisplay } from './KeyDisplay';
import type { CapabilityCatalog, IssuedLicense, Plan } from '../types';

const inputClass =
  'w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/50';

function emptyPlan(): Plan {
  return { id: '', name: '', tierLabel: '', description: null, features: {}, limits: {} };
}

function PlanEditor({
  plan,
  catalog,
  isNew,
  onSaved,
}: {
  plan: Plan;
  catalog: CapabilityCatalog;
  isNew: boolean;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<Plan>(plan);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => setDraft(plan), [plan]);

  const setFeature = (name: string, on: boolean) => {
    const features = { ...draft.features };
    if (on) features[name] = true;
    else delete features[name]; // absent = off on the hub
    setDraft({ ...draft, features });
  };

  const setLimit = (name: string, raw: string) => {
    const limits = { ...draft.limits };
    if (raw.trim() === '') delete limits[name]; // absent = hub's no-key baseline
    else limits[name] = parseInt(raw, 10);
    setDraft({ ...draft, limits });
  };

  const save = async () => {
    setIsSaving(true);
    setError('');
    try {
      await api.savePlan(draft);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save plan');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">Plan ID</label>
          <input
            className={inputClass}
            value={draft.id}
            disabled={!isNew}
            placeholder="pro-plus"
            onChange={(e) => setDraft({ ...draft, id: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">Name</label>
          <input className={inputClass} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </div>
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">Tier label</label>
          <select
            className={inputClass}
            value={draft.tierLabel}
            onChange={(e) => setDraft({ ...draft, tierLabel: e.target.value })}
          >
            <option value="" disabled>
              choose…
            </option>
            <option value="free">free</option>
            <option value="pro">pro</option>
            <option value="enterprise">enterprise</option>
          </select>
        </div>
      </div>

      <div>
        <p className="text-sm font-medium text-muted-foreground mb-2">Features</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1">
          {catalog.features.map((name) => (
            <label key={name} className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={draft.features[name] === true}
                onChange={(e) => setFeature(name, e.target.checked)}
              />
              <span className="font-mono text-xs">{name}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-medium text-muted-foreground mb-2">Limits (-1 = unlimited, blank = no-key baseline)</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {catalog.limits.map((name) => (
            <div key={name}>
              <label className="block text-xs font-mono text-muted-foreground mb-1">{name}</label>
              <input
                type="number"
                min={-1}
                className={inputClass}
                value={draft.limits[name] ?? ''}
                onChange={(e) => setLimit(name, e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={isSaving}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-medium px-4 py-2 rounded-md hover:bg-primary/90 transition-all text-sm disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {isSaving ? 'Saving...' : 'Save plan'}
        </button>
        <span className="text-xs text-muted-foreground">Applies on each bound hub's next lease refresh.</span>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

function IssueForm({ plans, onIssued }: { plans: Plan[]; onIssued: () => void }) {
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [planId, setPlanId] = useState(plans[0]?.id ?? '');
  const [durationDays, setDurationDays] = useState('');
  const [issuedKey, setIssuedKey] = useState<string | null>(null);
  const [isIssuing, setIsIssuing] = useState(false);

  const issue = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsIssuing(true);
    try {
      const result = await api.issueLicense({
        customerId: customerId.trim(),
        customerName: customerName.trim(),
        customerEmail: customerEmail.trim() || null,
        planId,
        durationDays: durationDays ? parseInt(durationDays, 10) : null,
      });
      setIssuedKey(result.licenseKey);
      onIssued();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to issue license');
    } finally {
      setIsIssuing(false);
    }
  };

  if (issuedKey) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <p className="text-sm font-medium text-foreground">Key issued. It binds to the first instance that redeems it.</p>
        <LicenseKeyDisplay licenseKey={issuedKey} />
      </div>
    );
  }

  return (
    <form onSubmit={issue} className="bg-card border border-border rounded-xl p-6 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <input className={inputClass} required placeholder="Customer ID" value={customerId} onChange={(e) => setCustomerId(e.target.value)} />
        <input className={inputClass} required placeholder="Customer name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
        <input className={inputClass} type="email" placeholder="Contact email (optional)" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <select className={inputClass} value={planId} onChange={(e) => setPlanId(e.target.value)}>
          {plans.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.id})
            </option>
          ))}
        </select>
        <input
          className={inputClass}
          type="number"
          min={1}
          placeholder="Duration days (blank = open-ended)"
          value={durationDays}
          onChange={(e) => setDurationDays(e.target.value)}
        />
        <button
          type="submit"
          disabled={isIssuing || !planId}
          className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-medium px-4 py-2 rounded-md hover:bg-primary/90 transition-all text-sm disabled:opacity-50"
        >
          {isIssuing ? 'Issuing...' : 'Issue key'}
        </button>
      </div>
    </form>
  );
}

export function PlanManager() {
  const [catalog, setCatalog] = useState<CapabilityCatalog | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [licenses, setLicenses] = useState<IssuedLicense[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [showIssue, setShowIssue] = useState(false);

  const load = async () => {
    try {
      const [cat, planList, licenseList] = await Promise.all([
        api.getCapabilities(),
        api.listPlans(),
        api.listIssuedLicenses(),
      ]);
      setCatalog(cat);
      setPlans(planList.plans);
      setLicenses(licenseList.licenses);
    } catch {
      // silently fail — the section just stays empty
    }
  };

  useEffect(() => {
    load();
  }, []);

  const changePlan = async (lic: IssuedLicense, planId: string) => {
    try {
      await api.updateLicenseEntitlement(lic.id, { planId });
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to change plan');
    }
  };

  if (!catalog) return null;
  const selected = plans.find((p) => p.id === selectedPlanId) ?? null;

  return (
    <div className="mt-12 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-accent uppercase tracking-wider">Plans</p>
          <p className="text-sm text-muted-foreground mt-1">
            Capability bundles carried in every lease (claims schema v{catalog.capsVersion}).
          </p>
        </div>
        <button
          onClick={() => {
            setCreating(!creating);
            setSelectedPlanId(null);
          }}
          className="inline-flex items-center gap-2 bg-accent text-white font-medium px-4 py-2 rounded-md hover:bg-accent/90 transition-all text-sm"
        >
          {creating ? <ChevronUp className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {creating ? 'Close' : 'New plan'}
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {plans.map((p) => (
          <button
            key={p.id}
            onClick={() => {
              setCreating(false);
              setSelectedPlanId(selectedPlanId === p.id ? null : p.id);
            }}
            className={`px-3 py-1.5 rounded-md border text-sm transition-colors ${
              selectedPlanId === p.id ? 'border-accent text-accent' : 'border-border text-foreground hover:bg-secondary'
            }`}
          >
            {p.name} <span className="text-xs text-muted-foreground font-mono">{p.tierLabel}</span>
          </button>
        ))}
      </div>

      {creating && <PlanEditor plan={emptyPlan()} catalog={catalog} isNew onSaved={() => { setCreating(false); load(); }} />}
      {selected && <PlanEditor plan={selected} catalog={catalog} isNew={false} onSaved={load} />}

      <div className="flex items-center justify-between pt-4">
        <div>
          <p className="text-sm font-medium text-accent uppercase tracking-wider">Issued keys</p>
          <p className="text-sm text-muted-foreground mt-1">Free, Pro and manually issued online keys.</p>
        </div>
        <button
          onClick={() => setShowIssue(!showIssue)}
          className="inline-flex items-center gap-2 bg-accent text-white font-medium px-4 py-2 rounded-md hover:bg-accent/90 transition-all text-sm"
        >
          {showIssue ? <ChevronUp className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showIssue ? 'Close' : 'Issue key'}
        </button>
      </div>

      {showIssue && plans.length > 0 && <IssueForm plans={plans} onIssued={load} />}

      <div className="space-y-2">
        {licenses.map((lic) => (
          <div
            key={lic.id}
            className={`bg-card border border-border rounded-xl p-4 flex items-center justify-between gap-4 ${lic.revoked ? 'opacity-50' : ''}`}
          >
            <div className="min-w-0">
              <div className="text-sm text-foreground truncate">
                {lic.customerName || lic.customerEmail || lic.customerId}
                <span className="ml-2 text-xs text-muted-foreground font-mono">{lic.type}</span>
                {lic.overrides && <span className="ml-2 text-xs text-accent">overrides</span>}
              </div>
              <div className="text-xs text-muted-foreground">
                {lic.instanceId ? `bound to ${lic.instanceId}` : 'not activated'}
                {lic.expiresAt && ` · expires ${new Date(lic.expiresAt).toLocaleDateString()}`}
              </div>
            </div>
            <select
              className="bg-input border border-border rounded-md px-2 py-1 text-sm text-foreground"
              value={lic.planId ?? ''}
              disabled={lic.revoked}
              onChange={(e) => changePlan(lic, e.target.value)}
            >
              {!lic.planId && <option value="">(type default)</option>}
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}
